// The single central state object. Everything the simulation needs lives here
// and nothing else holds authoritative game data, so the whole thing serializes
// to one JSON blob for save/resume. Derived values (run modifiers, meta perks)
// are recomputed on load rather than trusted from disk, which keeps old saves
// valid when balance numbers change.

import { RESOURCES, RESOURCE_ORDER, type ResourceId } from '@data/resources';
import { BUILDING_IDS, type BuildingId } from '@data/buildings';
import { BALANCE } from '@data/balance';
import { VERSION } from '@data/version';
import { freshRunModifiers, CARD_BY_ID, activeSynergies, type RunModifiers } from '@data/cards';
import { TECH_BY_ID } from '@data/technologies';
import { ARTIFACT_BY_ID } from '@data/artifacts';
import { computeMetaPerks, type MetaPerks } from '@data/skills';

/** An artifact dropped by a boss this run, and whether it has been activated. */
export interface RunArtifact {
  id: string;
  activated: boolean;
}

/** A timed buff (e.g. the Production Surge tech). */
export interface Buff {
  id: string;
  label: string;
  icon: string;
  prodMult: number;
  expiresAt: number; // gameTime at which it ends
}

/** A harvestable resource node on the iso grid. */
export interface WorldNode {
  id: number;
  resource: ResourceId;
  gx: number; // tile coordinate
  gy: number;
  hp: number;
  maxHp: number;
  depleted: boolean;
  /** gameTime (seconds) at which a depleted node respawns; 0 when alive. */
  respawnAt: number;
}

/** Everything that resets when the settlement falls. */
export interface RunState {
  age: number; // 0 = first age
  resources: Record<ResourceId, number>;
  /** Lifetime-this-run weighted resource total, drives age thresholds. */
  progress: number;
  population: number;
  buildings: Record<BuildingId, number>; // level per building type
  buildingCount: number; // total levels built (settlement size)
  settlementTier: number; // index into BALANCE.settlementTiers
  modifiers: RunModifiers; // derived from pickedCards (+ recomputed on load)
  pickedCards: string[]; // card ids chosen, in order
  spoils: number; // run currency
  wave: number; // waves cleared so far (raiders + gate bosses)
  wavesThisAge: number; // raider waves faced since entering the current age
  // (drives the within-age threat ramp; resets to 0 on an age advance)
  nextWaveAt: number; // gameTime at which the next attack telegraphs
  /** gameTime at which a telegraphed wave resolves; 0 means no wave pending. */
  combatResolveAt: number;
  /** True while the era boss has been summoned to the gate (progress crossed the
   *  age threshold) and not yet defeated. The next wave to telegraph is the boss. */
  gatePending: boolean;
  /** True when the currently telegraphed/resolving wave is the era gate boss
   *  (latched when the telegraph starts, so resolution matches the banner). */
  combatIsBoss: boolean;
  nodes: WorldNode[];
  autoUnlocked: boolean; // has auto-collection switched on this run
  techs: Record<string, number>; // special technologies owned (count per id)
  artifacts: RunArtifact[]; // boss-dropped artifacts this run
  buffs: Buff[]; // active timed buffs
  conquered: boolean; // has this planet's conquest goal been reached (prestige ready)
  /** gameTime of the next random calamity; 0 = not yet scheduled (lazy seed). */
  nextCalamityAt: number;
}

/** Everything that persists across runs. */
export interface MetaState {
  skillPoints: number;
  skillLevels: Record<string, number>;
  totalRuns: number;
  bestWave: number;
  bestAge: number;
  cosmic: number; // planetary prestige currency: global multipliers
  planet: number; // current planet index (0-based); higher = harder
  planetsConquered: number; // lifetime planets conquered
}

/** Transient UI gates that pause the sim (awaiting player input). */
export interface PendingState {
  /** Card ids offered on an age advance, or null when none pending. */
  cardChoices: string[] | null;
  /** True after a lost wave: show the skill tree before restart. */
  dead: boolean;
  deathWave: number;
  deathSkillPoints: number;
  /** True when the run ended by choice (retire) rather than a lost wave. */
  retired: boolean;
}

export interface GameSettings {
  muted: boolean;
}

export interface GameState {
  version: string;
  gameTime: number; // accumulated simulation seconds
  lastSavedWall: number; // Date.now() at last save, for offline accrual
  run: RunState;
  meta: MetaState;
  pending: PendingState;
  settings: GameSettings;
  /** Derived, recomputed on load and after spending skill points. */
  metaPerks: MetaPerks;
}

function zeroResources(): Record<ResourceId, number> {
  const out = {} as Record<ResourceId, number>;
  for (const id of RESOURCE_ORDER) out[id] = 0;
  return out;
}

function zeroBuildings(): Record<BuildingId, number> {
  const out = {} as Record<BuildingId, number>;
  for (const id of BUILDING_IDS) out[id] = 0;
  return out;
}

/** Chebyshev distance from the settlement core, used to keep nodes off-center. */
function chebyshev(x: number, y: number): number {
  return Math.max(Math.abs(x), Math.abs(y));
}

/** Places resource nodes on a ring around the settlement core. Wood and stone
 *  are plentiful; ore is rarer and (via its high base HP) needs more total
 *  damage to crack, teaching the "deeper resources cost more" curve. */
export function generateNodes(age: number): WorldNode[] {
  const nodes: WorldNode[] = [];
  const used = new Set<string>();
  const ageHpMult = 1 + age * 0.12;
  // More nodes, spread across a wider, more open field (the settlement fills
  // the inner rings; resources ring it from a comfortable distance).
  const plan: { res: ResourceId; count: number }[] = [
    { res: 'wood', count: 7 },
    { res: 'stone', count: 5 },
    { res: 'ore', count: 3 },
  ];
  let id = 1;
  for (const { res, count } of plan) {
    const def = RESOURCES[res];
    for (let i = 0; i < count; i++) {
      let gx = 0;
      let gy = 0;
      // reject tiles too close to the settlement core or already taken
      for (let attempt = 0; attempt < 60; attempt++) {
        gx = Math.floor(Math.random() * 19) - 9; // -9..9
        gy = Math.floor(Math.random() * 19) - 9;
        const d = chebyshev(gx, gy);
        if (d >= 4 && d <= 8 && !used.has(`${gx},${gy}`)) break;
      }
      used.add(`${gx},${gy}`);
      const maxHp = Math.round(def.nodeHp * ageHpMult);
      nodes.push({
        id: id++,
        resource: res,
        gx,
        gy,
        hp: maxHp,
        maxHp,
        depleted: false,
        respawnAt: 0,
      });
    }
  }
  return nodes;
}

/** Builds a fresh run, seeding starting resources and population from the
 *  permanent skill-tree perks. `startTime` is the current gameTime so the first
 *  wave is scheduled relative to it (gameTime is monotonic across runs). */
export function createRun(perks: MetaPerks, startTime = 0): RunState {
  const resources = zeroResources();
  resources.wood = perks.startWood;
  resources.stone = perks.startStone;
  resources.ore = perks.startOre;
  return {
    age: 0,
    resources,
    progress: 0,
    population: BALANCE.population.base + perks.startPopulation,
    buildings: zeroBuildings(),
    buildingCount: 0,
    settlementTier: 0,
    modifiers: freshRunModifiers(),
    pickedCards: [],
    spoils: perks.startSpoils,
    wave: 0,
    wavesThisAge: 0,
    nextWaveAt: startTime + BALANCE.waves.firstWaveDelay,
    combatResolveAt: 0,
    gatePending: false,
    combatIsBoss: false,
    nodes: generateNodes(0),
    autoUnlocked: false,
    techs: {},
    artifacts: [],
    buffs: [],
    conquered: false,
    nextCalamityAt: 0, // seeded lazily by the calamity system
  };
}

/** Recomputes run.modifiers from everything that feeds them: picked cards, the
 *  synergies those picks activate, purchased technologies, and activated
 *  artifacts. Called after any of those change and on load, so the live
 *  modifiers always match current definitions. */
export function recomputeModifiers(run: RunState): void {
  const mods = freshRunModifiers();
  // 1. Age cards
  for (const id of run.pickedCards) {
    const card = CARD_BY_ID[id];
    if (card) card.apply(mods);
  }
  // 2. Synergies unlocked by the card combination
  for (const syn of activeSynergies(run.pickedCards)) syn.apply(mods);
  // 3. Special technologies (permanent-for-run ones; surge is buff-only)
  for (const [techId, count] of Object.entries(run.techs)) {
    const tech = TECH_BY_ID[techId];
    if (tech?.apply && count > 0) {
      for (let i = 0; i < count; i++) tech.apply(mods, i + 1);
    }
  }
  // 4. Activated artifacts
  for (const art of run.artifacts) {
    if (art.activated) ARTIFACT_BY_ID[art.id]?.apply(mods);
  }
  run.modifiers = mods;
}

/** A brand-new save (no prior progress). */
export function createInitialState(): GameState {
  const meta: MetaState = {
    skillPoints: 0,
    skillLevels: {},
    totalRuns: 0,
    bestWave: 0,
    bestAge: 0,
    cosmic: 0,
    planet: 0,
    planetsConquered: 0,
  };
  const perks = computeMetaPerks(meta.skillLevels, BALANCE.offline.baseCapHours);
  return {
    version: VERSION,
    gameTime: 0,
    lastSavedWall: Date.now(),
    run: createRun(perks),
    meta,
    pending: { cardChoices: null, dead: false, deathWave: 0, deathSkillPoints: 0, retired: false },
    settings: { muted: false },
    metaPerks: perks,
  };
}

/** Recomputes all derived fields (meta perks, run modifiers). Call after load
 *  and after any change to skill levels. */
export function refreshDerived(state: GameState): void {
  state.metaPerks = computeMetaPerks(state.meta.skillLevels, BALANCE.offline.baseCapHours);
  recomputeModifiers(state.run);
}
